import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Library, Loader2, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { errMsg, listAssets } from '../../../lib/aiStudioApi';
import { cn } from '../../../lib/utils';
import { AssetTile, AssetUploadButton, FOLDER_ICON } from './AssetTile';

/** Pick (or upload) a reusable asset from the Studio Asset Library — used in every production. */
export function AssetPicker({ open, onOpenChange, folders, onPick, title = 'Pick from your Asset Library' }) {
  const [folder, setFolder] = useState(folders[0]);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const load = useCallback(() => listAssets({ folder, q }).then(setData).catch((e) => toast.error(errMsg(e))), [folder, q]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (open) setFolder(folders[0]); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const labels = Object.fromEntries((data?.folders || []).map((f) => [f.id, f]));
  const pick = (a) => { onPick(a); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-dark max-w-4xl border-neutral-800 bg-neutral-950 text-neutral-100" data-testid="asset-picker">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Library size={16} className="text-amber-300" /> {title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          {folders.map((f) => {
            const Icon = FOLDER_ICON[f];
            return (
              <button key={f} type="button" onClick={() => setFolder(f)} data-testid={`asset-picker-folder-${f}`}
                className={cn('flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors duration-200',
                  folder === f ? 'border-amber-400 bg-amber-500/15 text-amber-100' : 'border-neutral-700 text-neutral-300 hover:border-neutral-500')}>
                {Icon && <Icon size={12} />} {labels[f]?.label || f} <span className="font-mono text-[10px] text-neutral-500">{labels[f]?.count ?? ''}</span>
              </button>
            );
          })}
          <div className="relative ml-auto w-48">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="h-8 border-neutral-700 bg-neutral-900 pl-7 text-xs" data-testid="asset-picker-search" />
          </div>
          <AssetUploadButton folder={folder} onUploaded={() => load()} testId="asset-picker-upload" />
        </div>
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {!data && <div className="flex items-center gap-2 p-6 text-sm text-neutral-400"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {data && !data.items.length && (
            <div className="rounded-xl border border-dashed border-neutral-800 p-10 text-center text-sm text-neutral-400" data-testid="asset-picker-empty">
              Nothing in {labels[folder]?.label || folder} yet — upload one above. Images are shrunk automatically.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {(data?.items || []).map((a) => (
              <AssetTile key={a.id} asset={a} onClick={() => pick(a)} testId={`asset-picker-item-${a.id}`}>
                <Button size="sm" onClick={() => pick(a)} className="h-7 w-full bg-neutral-800 text-[11px] text-neutral-100 hover:bg-amber-500 hover:text-neutral-900" data-testid={`asset-picker-use-${a.id}`}>
                  <Check size={12} className="mr-1" /> Use this
                </Button>
              </AssetTile>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
